import { Playbook, PlaybookCategory, PlaybookMeta } from '@/types/playbook/playbook';
import { SetupAnalysisResult } from '@/types/agent';
import { stripEmojis } from '@/utils/formatting/emoji';
import {
  getPlaybookById,
  getPlaybookBySlug,
  getPlaybooksByCategory,
  getAllPlaybooks,
  getFeaturedPlaybooks,
  getRelatedPlaybooks,
  searchPlaybooks,
  getPlaybookCategories,
  getCategoryById,
  getCategoryBySlug
} from '@/data/playbooks/registry';

export class PlaybookService {
  // Playbook retrieval methods
  static getPlaybook(idOrSlug: string): Playbook | undefined {
    // Try by ID first, then by slug
    return getPlaybookById(idOrSlug) || getPlaybookBySlug(idOrSlug);
  }

  static getPlaybooksByCategory(categoryIdOrSlug: string): Playbook[] {
    // Support both category ID and slug
    const category = getCategoryById(categoryIdOrSlug) || getCategoryBySlug(categoryIdOrSlug);
    if (!category) return [];
    return getPlaybooksByCategory(category.id);
  }

  static getAllPlaybooks(): Playbook[] {
    return getAllPlaybooks();
  }

  static getFeaturedPlaybooks(limit?: number): Playbook[] {
    const featured = getFeaturedPlaybooks();
    return limit ? featured.slice(0, limit) : featured;
  }

  static getRelatedPlaybooks(playbookIdOrSlug: string, limit = 3): Playbook[] {
    const playbook = this.getPlaybook(playbookIdOrSlug);
    if (!playbook) return [];
    return getRelatedPlaybooks(playbook.meta.id, limit);
  }

  static searchPlaybooks(query: string): Playbook[] {
    return searchPlaybooks(query);
  }

  // Category methods
  static getCategories(): PlaybookCategory[] {
    return getPlaybookCategories();
  }

  static getCategory(idOrSlug: string): PlaybookCategory | undefined {
    return getCategoryById(idOrSlug) || getCategoryBySlug(idOrSlug);
  }

  static getCategoriesWithPlaybooks(): Array<PlaybookCategory & { playbooks: Playbook[] }> {
    return this.getCategories().map(category => ({
      ...category,
      playbooks: this.getPlaybooksByCategory(category.id)
    }));
  }

  // URL generation helpers
  static getPlaybookUrl(playbook: Playbook | PlaybookMeta): string {
    const slug = 'meta' in playbook ? playbook.meta.slug : playbook.slug;
    const categoryId = 'meta' in playbook ? playbook.meta.categoryId : playbook.categoryId;
    const category = this.getCategory(categoryId);
    return `/playbooks/${category?.slug || categoryId}/${slug}`;
  }

  static getCategoryUrl(category: PlaybookCategory | string): string {
    const cat = typeof category === 'string' ? this.getCategory(category) : category;
    return `/playbooks/${cat?.slug || ''}`;
  }

  // Utility methods
  static getPlaybookStats(playbook: Playbook): {
    hasDemo: boolean;
    hasFlowSteps: boolean;
    hasTestimonials: boolean;
    hasResources: boolean;
    completeness: number;
  } {
    const hasDemo = !!(playbook.demoConfig && playbook.demoScript);
    const hasFlowSteps = !!(playbook.flowSteps && playbook.flowSteps.length > 0);
    const hasTestimonials = !!(playbook.testimonials && playbook.testimonials.length > 0);
    const hasResources = !!(playbook.resources && playbook.resources.length > 0);
    
    const completeness = [
      playbook.meta,
      playbook.seo,
      hasDemo,
      playbook.templateConfig,
      hasFlowSteps,
      playbook.metrics,
      hasTestimonials,
      hasResources
    ].filter(Boolean).length / 8 * 100;

    return {
      hasDemo,
      hasFlowSteps,
      hasTestimonials,
      hasResources,
      completeness
    };
  }

  // Template generation helper
  static generateAgentConfigFromPlaybook(playbook: Playbook): SetupAnalysisResult {
    const templateConfig = playbook.templateConfig;
    const meta = playbook.meta;

    const category = meta.categoryId.includes('support') ? 'support' :
                     meta.categoryId.includes('onboard') ? 'onboarding' : 'sales';

    return {
      genieName: stripEmojis(meta.name),
      useCase: stripEmojis(meta.strategy),
      systemPrompt: stripEmojis(templateConfig?.systemPrompt || "You are a helpful assistant."),
      firstMessage: stripEmojis(templateConfig?.firstMessage || "Hi! How can I help you today?"),
      webWelcomeMessage: stripEmojis(templateConfig?.firstMessage || "Hi! How can I help you today?"),
      language: templateConfig?.language || 'en',
      voicePreference: 'professional',
      voiceId: templateConfig?.voiceId || 'hpp4J3VqNfWAUOO0d1Us',
      category,
      description: stripEmojis(meta.strategy),
      conversationSettings: {
        maxDuration: 600,
        turnTimeout: 10,
        silenceTimeout: 300,
        turnMode: 'silence'
      },
      confidence: 0.95
    };
  }

  // Filtering and sorting
  static filterPlaybooks(options: {
    categories?: string[];
    tags?: string[];
    difficulty?: string[];
    featured?: boolean;
  }): Playbook[] {
    let playbooks = this.getAllPlaybooks();

    if (options.categories && options.categories.length > 0) {
      playbooks = playbooks.filter(p => options.categories!.includes(p.meta.categoryId));
    }

    if (options.tags && options.tags.length > 0) {
      playbooks = playbooks.filter(p => 
        p.meta.tags?.some(tag => options.tags!.includes(tag))
      );
    }

    if (options.difficulty && options.difficulty.length > 0) {
      playbooks = playbooks.filter(p => 
        p.meta.difficulty && options.difficulty!.includes(p.meta.difficulty)
      );
    }

    if (options.featured !== undefined) {
      playbooks = playbooks.filter(p => p.meta.isFeatured === options.featured);
    }

    return playbooks;
  }

  static sortPlaybooks(playbooks: Playbook[], sortBy: 'name' | 'difficulty' | 'setupTime' | 'featured' = 'name'): Playbook[] {
    const sorted = [...playbooks];

    switch (sortBy) {
      case 'name':
        return sorted.sort((a, b) => a.meta.name.localeCompare(b.meta.name));
      
      case 'difficulty':
        const difficultyOrder = { 'beginner': 0, 'intermediate': 1, 'advanced': 2 };
        return sorted.sort((a, b) => {
          const aOrder = difficultyOrder[a.meta.difficulty || 'intermediate'];
          const bOrder = difficultyOrder[b.meta.difficulty || 'intermediate'];
          return aOrder - bOrder;
        });
      
      case 'setupTime':
        return sorted.sort((a, b) => {
          const aTime = parseInt(a.meta.estimatedSetupTime || '999');
          const bTime = parseInt(b.meta.estimatedSetupTime || '999');
          return aTime - bTime;
        });
      
      case 'featured':
        return sorted.sort((a, b) => {
          if (a.meta.isFeatured && !b.meta.isFeatured) return -1;
          if (!a.meta.isFeatured && b.meta.isFeatured) return 1;
          return 0;
        });
      
      default:
        return sorted;
    }
  }
}